import React, { useEffect, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { theme } from '../../../../theme';

const MessagingThreadView = ({
  identity,
  subtitle,
  status,
  messages = [],
  ownSenderType,
  draft = '',
  onDraftChange,
  onSend,
  sending = false,
  error = '',
  onBack,
  actions,
  context,
}) => {
  const messagesEndRef = useRef(null);
  const closed = status === 'closed';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages]);

  if (!identity) {
    return (
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          height: '100%',
          px: 4,
          textAlign: 'center',
          background: `linear-gradient(145deg, ${theme.colors.secondary[50]}, ${theme.colors.primary[50]})`,
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 76,
              height: 76,
              mx: 'auto',
              mb: 2,
              borderRadius: '24px',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: theme.colors.primary[800],
              boxShadow: '0 14px 35px rgba(107, 56, 26, 0.10)',
            }}
          >
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
            Sélectionnez une discussion
          </Typography>
          <Typography color="text.secondary">
            Choisissez une conversation pour retrouver vos messages.
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (draft.trim() && !closed && !sending) onSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        backgroundColor: theme.colors.secondary[50],
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
          px: { xs: 1.25, sm: 2 },
          py: 1.5,
          borderBottom: `1px solid ${theme.colors.secondary[200]}`,
          backgroundColor: 'rgba(255, 255, 255, 0.88)',
        }}
      >
        {onBack && (
          <IconButton
            aria-label="Retour aux discussions"
            onClick={onBack}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: theme.colors.primary[950],
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
        )}
        <Avatar
          sx={{
            width: 44,
            height: 44,
            backgroundColor: theme.colors.secondary[300],
            color: theme.colors.secondary[950],
            fontWeight: 800,
          }}
        >
          {identity.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 800 }}>
            {identity}
          </Typography>
          <Typography
            noWrap
            variant="caption"
            sx={{ color: theme.colors.grey[600] }}
          >
            {subtitle || (
              closed ? 'Conversation fermée' : 'Discussion en cours'
            )}
          </Typography>
        </Box>
        {actions && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexShrink: 0,
            }}
          >
            {actions}
          </Box>
        )}
      </Box>

      {context && (
        <Box sx={{ flexShrink: 0 }}>
          {context}
        </Box>
      )}

      <Box
        aria-live="polite"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: { xs: 1.5, sm: 2.5 },
          py: 2.5,
          background: `linear-gradient(180deg, ${theme.colors.secondary[50]} 0%, ${theme.colors.primary[50]} 100%)`,
        }}
      >
        {!messages.length && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary" variant="body2">
              La discussion peut commencer ici.
            </Typography>
          </Box>
        )}
        {messages.map((message) => {
          const own = message.sender_type === ownSenderType;
          return (
            <Box
              key={message.id}
              data-testid={`message-${message.id}`}
              data-message-owner={own ? 'own' : 'incoming'}
              sx={{
                display: 'flex',
                justifyContent: own ? 'flex-end' : 'flex-start',
                mb: 1.25,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  maxWidth: { xs: '86%', sm: '74%' },
                  px: 1.75,
                  py: 1.15,
                  borderRadius: own
                    ? '17px 17px 5px 17px'
                    : '17px 17px 17px 5px',
                  backgroundColor: own
                    ? theme.colors.primary[800]
                    : theme.colors.secondary[100],
                  color: own ? 'white' : theme.colors.grey[900],
                  border: own
                    ? 'none'
                    : `1px solid ${theme.colors.secondary[200]}`,
                  boxShadow: own
                    ? '0 7px 18px rgba(140, 80, 42, 0.16)'
                    : '0 5px 15px rgba(67, 44, 23, 0.07)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {message.content}
                </Typography>
              </Paper>
            </Box>
          );
        })}
        <Box ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.25, sm: 2 },
          py: 1.5,
          borderTop: `1px solid ${theme.colors.secondary[200]}`,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {closed && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.75, color: theme.colors.grey[600] }}
          >
            Cette conversation est fermée. Son historique reste disponible.
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              closed ? 'Conversation fermée' : 'Écrivez votre message…'
            }
            disabled={closed || sending}
            inputProps={{ 'aria-label': 'Écrivez votre message' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '18px',
                backgroundColor: theme.colors.secondary[50],
                '& fieldset': {
                  borderColor: theme.colors.secondary[200],
                },
                '&:hover fieldset, &.Mui-focused fieldset': {
                  borderColor: theme.colors.primary[600],
                },
              },
            }}
          />
          <IconButton
            aria-label="Envoyer"
            onClick={onSend}
            disabled={!draft.trim() || closed || sending}
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              backgroundColor: theme.colors.primary[800],
              color: 'white',
              '&:hover': {
                backgroundColor: theme.colors.primary[900],
              },
              '&.Mui-disabled': {
                backgroundColor: theme.colors.grey[200],
                color: theme.colors.grey[500],
              },
            }}
          >
            {sending ? (
              <CircularProgress size={19} color="inherit" />
            ) : (
              <SendRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default MessagingThreadView;
